import React, { useState, useEffect, useCallback } from "react";
import {
    Box, Button, Typography, TextField, InputAdornment, Card, CardContent,
    Chip, Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete,
    MenuItem, TableContainer, Table, TableHead, TableRow, TableCell, TableBody,
    IconButton, Select, Snackbar, Alert, InputLabel, FormControl, Menu,
    Skeleton, Tooltip,
} from "@mui/material";
import AddRoundedIcon            from "@mui/icons-material/AddRounded";
import FmdGoodRoundedIcon        from "@mui/icons-material/FmdGoodRounded";
import MapRoundedIcon            from "@mui/icons-material/MapRounded";
import SearchRoundedIcon         from "@mui/icons-material/SearchRounded";
import ClearRoundedIcon          from "@mui/icons-material/ClearRounded";
import LocationCityRoundedIcon   from "@mui/icons-material/LocationCityRounded";
import MoreVertIcon              from "@mui/icons-material/MoreVert";
import CheckCircleOutline        from "@mui/icons-material/CheckCircleOutline";
import BlockOutlined             from "@mui/icons-material/BlockOutlined";
import RestartAltRoundedIcon     from "@mui/icons-material/RestartAltRounded";
import KeyboardArrowLeft         from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight        from "@mui/icons-material/KeyboardArrowRight";
import usePlacesAutocomplete, { getGeocode, getLatLng } from "use-places-autocomplete";
import { getData, postData, API_ENDPOINTS, putData } from "../../services/apiService";
import { useNavigate } from "react-router-dom";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
    teal:    "#007C84", tealDk: "#016268", tealLt: "#E0F4F5",
    orange:  "#FF7900", orangeLt: "#FFF0E0",
    emerald: "#059669", emeraldLt: "#D1FAE5",
    rose:    "#E11D48", roseLt:   "#FFE4E6",
    indigo:  "#4F46E5", indigoLt: "#EEF2FF",
    amber:   "#D97706", amberLt:  "#FEF3C7",
    bg:      "#F7FAFC", border: "#E2E8F0",
    text:    "#0F172A", muted:  "#64748B",
};

// ── Tab pill ──────────────────────────────────────────────────────────────
const TabBtn = ({ label, count, active, onClick }) => (
    <Box onClick={onClick} sx={{
        display: "flex", alignItems: "center", gap: 1,
        px: 2.5, py: 1.2, borderRadius: "10px", cursor: "pointer",
        bgcolor: active ? T.teal : "transparent", color: active ? "#fff" : T.muted,
        fontWeight: active ? 700 : 500, fontSize: 13, transition: "all 0.15s",
        "&:hover": { bgcolor: active ? T.teal : T.tealLt, color: active ? "#fff" : T.teal },
        fontFamily: "'Plus Jakarta Sans', sans-serif",
    }}>
        {label}
        <Box component="span" sx={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            minWidth: 20, height: 20, borderRadius: "6px", fontSize: 11, fontWeight: 700,
            bgcolor: active ? "rgba(255,255,255,0.25)" : T.border,
            color:   active ? "#fff" : T.muted, px: "4px",
        }}>{count}</Box>
    </Box>
);

// ── State badge colors ────────────────────────────────────────────────────
const STATE_COLORS = [
    [T.tealLt, T.teal], [T.orangeLt, T.orange], [T.indigoLt, T.indigo],
    [T.emeraldLt, T.emerald], [T.amberLt, T.amber], [T.roseLt, T.rose],
];
const stateColor = (name = "") => STATE_COLORS[name.charCodeAt(0) % STATE_COLORS.length];

// ── City Card ─────────────────────────────────────────────────────────────
const CityCard = ({ id, name, state, status, boardingCount, onStatusUpdate }) => {
    const [anchor, setAnchor] = useState(null);
    const [bg, fg] = stateColor(state || name);
    const isActive = status === "active";

    return (
        <Card sx={{
            borderRadius: "16px", border: `1px solid ${T.border}`,
            boxShadow: "0 1px 4px rgba(15,23,42,0.06)", overflow: "hidden",
            transition: "transform 0.18s, box-shadow 0.18s",
            "&:hover": { transform: "translateY(-2px)", boxShadow: "0 6px 16px rgba(0,124,132,0.10)" },
        }}>
            <Box sx={{ height: 3, background: `linear-gradient(90deg, ${T.teal}, ${T.tealLt})` }} />
            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                    <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                        <Box sx={{ width: 44, height: 44, borderRadius: "12px", bgcolor: bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <LocationCityRoundedIcon sx={{ fontSize: 22, color: fg }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: 14, fontWeight: 800, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{name || "N/A"}</Typography>
                            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, bgcolor: bg, borderRadius: "6px", px: "6px", py: "1px", mt: 0.3 }}>
                                <Typography sx={{ fontSize: 10, fontWeight: 700, color: fg }}>{state || "Unknown"}</Typography>
                            </Box>
                        </Box>
                    </Box>
                    <IconButton size="small" onClick={e => setAnchor(e.currentTarget)}>
                        <MoreVertIcon sx={{ fontSize: 18, color: T.muted }} />
                    </IconButton>
                    <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
                        PaperProps={{ sx: { borderRadius: "12px", border: `1px solid ${T.border}`, boxShadow: "0 4px 16px rgba(0,0,0,0.1)" } }}>
                        <MenuItem onClick={() => { onStatusUpdate(id, name, status); setAnchor(null); }} sx={{ fontSize: 13, color: isActive ? T.rose : T.emerald, fontWeight: 600 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                {isActive ? <BlockOutlined fontSize="small" /> : <CheckCircleOutline fontSize="small" />}
                                {isActive ? "Mark Inactive" : "Mark Active"}
                            </Box>
                        </MenuItem>
                    </Menu>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                        <FmdGoodRoundedIcon sx={{ fontSize: 14, color: T.muted }} />
                        <Typography sx={{ fontSize: 12, color: T.muted, fontWeight: 500 }}>
                            {boardingCount || 0} boarding point{boardingCount !== 1 ? "s" : ""}
                        </Typography>
                    </Box>
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1, py: 0.3, borderRadius: "7px", bgcolor: isActive ? T.emeraldLt : "#F1F5F9", border: `1px solid ${isActive ? T.emerald : T.border}22` }}>
                        <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: isActive ? T.emerald : T.muted }} />
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: isActive ? T.emerald : T.muted, textTransform: "uppercase" }}>
                            {isActive ? "Active" : "Inactive"}
                        </Typography>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

// ── Empty state ───────────────────────────────────────────────────────────
const Empty = ({ icon: Icon, msg, sub }) => (
    <Box sx={{ textAlign: "center", py: 8 }}>
        <Icon sx={{ fontSize: 48, color: T.border, mb: 1 }} />
        <Typography sx={{ fontWeight: 700, color: T.text, mb: 0.5 }}>{msg}</Typography>
        <Typography sx={{ fontSize: 13, color: T.muted }}>{sub}</Typography>
    </Box>
);

// ── Main component ────────────────────────────────────────────────────────
function LocationManagement() {
    const navigate = useNavigate();
    const [tab,         setTab]         = useState(0);
    const [citySearch,  setCitySearch]  = useState("");
    const [cities,      setCities]      = useState([]);
    const [citiesCount, setCitiesCount] = useState(0);
    const [loading,     setLoading]     = useState(false);
    const [openCityDialog, setOpenCityDialog] = useState(false);
    const [openBPDialog,   setOpenBPDialog]   = useState(false);
    const [isSubmitting,   setIsSubmitting]   = useState(false);

    const [boardingPoints, setBoardingPoints] = useState([]);
    const [bpCount,        setBpCount]        = useState(0);
    const [bpFilters, setBpFilters] = useState({ search: "", city_id: "", status: "" });

    const [bpMenuAnchor, setBpMenuAnchor] = useState(null);
    const [activeRow,    setActiveRow]    = useState(null);

    const [currentPage,  setCurrentPage]  = useState(0);
    const [totalPages,   setTotalPages]   = useState(1);
    const [perPage,      setPerPage]      = useState(10);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const [cityForm, setCityForm] = useState({ city: "", state: "", latitude: "", longitude: "" });
    const [bpForm,   setBpForm]   = useState({ city_id: null, cityName: "", point: "", address: "", latitude: "", longitude: "", position: "" });

    const [confirmDialog,   setConfirmDialog]   = useState({ open: false, cityId: null, cityName: "", currentStatus: "" });
    const [confirmBPDialog, setConfirmBPDialog] = useState({ open: false, id: null, pointName: "", currentStatus: "" });

    const toast = useCallback((message, severity = "success") => setSnackbar({ open: true, message, severity }), []);

    const cityAC = usePlacesAutocomplete({ requestOptions: { types: ["(cities)"], componentRestrictions: { country: "in" } }, debounce: 300 });
    const bpAC   = usePlacesAutocomplete({ requestOptions: { componentRestrictions: { country: "in" } }, debounce: 300 });

    const fetchCities = useCallback(async (q = "") => {
        setLoading(true);
        try {
            const r = await getData(API_ENDPOINTS.getCitiesData, { search: q });
            if (r.status === "SUCCESS") { setCities(r.result.cities || []); setCitiesCount(r.result.totalCities || 0); }
        } catch (_) {} finally { setLoading(false); }
    }, []);

    const fetchBP = useCallback(async () => {
        setLoading(true);
        try {
            const r = await getData(API_ENDPOINTS.getAllBoardingPoints, { ...bpFilters, page: currentPage + 1, limit: perPage });
            if (r.status === "SUCCESS") { setBoardingPoints(r.result.boardingDropPoints || []); setBpCount(r.result.totalBoardingPoints || 0); setTotalPages(r.result.totalPages || 1); }
        } catch (_) {} finally { setLoading(false); }
    }, [bpFilters, currentPage, perPage]);

    useEffect(() => { fetchCities(""); fetchBP(); }, [fetchCities, fetchBP]);
    useEffect(() => { if (tab === 0) { const t = setTimeout(() => fetchCities(citySearch), 500); return () => clearTimeout(t); } }, [citySearch, tab, fetchCities]);

    const handleSelectCity = async (_, v) => {
        if (!v) return;
        cityAC.setValue(v.description, false); cityAC.clearSuggestions();
        try {
            const res = await getGeocode({ address: v.description });
            const { lat, lng } = await getLatLng(res[0]);
            const st   = res[0].address_components.find(c => c.types.includes("administrative_area_level_1"));
            const city = res[0].address_components.find(c => c.types.includes("locality")) || res[0].address_components[0];
            setCityForm({ city: city.long_name, state: st?.long_name || "", latitude: lat.toString(), longitude: lng.toString() });
        } catch (_) {}
    };

    const handleSelectBP = async (_, v) => {
        if (!v) return;
        bpAC.setValue(v.description, false); bpAC.clearSuggestions();
        try {
            const res = await getGeocode({ address: v.description });
            const { lat, lng } = await getLatLng(res[0]);
            setBpForm(p => ({ ...p, point: v.description, address: res[0].formatted_address, latitude: lat.toString(), longitude: lng.toString() }));
        } catch (_) {}
    };

    const closeCityDialog = () => { setOpenCityDialog(false); setCityForm({ city: "", state: "", latitude: "", longitude: "" }); cityAC.setValue(""); };
    const closeBPDialog   = () => { setOpenBPDialog(false);   setBpForm({ city_id: null, cityName: "", point: "", address: "", latitude: "", longitude: "", position: "" }); bpAC.setValue(""); };

    const handleAddCity = async () => {
        if (!cityForm.city) return;
        setIsSubmitting(true);
        try {
            const r = await postData(API_ENDPOINTS.insertCityData, { ...cityForm, status: "active" });
            if (r.status === "SUCCESS") { toast("City added!"); closeCityDialog(); fetchCities(); }
        } catch (e) { toast(e.response?.data?.message || "Error", "error"); } finally { setIsSubmitting(false); }
    };

    const handleAddBP = async () => {
        if (!bpForm.city_id || !bpForm.point) return;
        setIsSubmitting(true);
        try {
            const r = await postData(API_ENDPOINTS.insertBoardingDropPoint, { ...bpForm, position: parseInt(bpForm.position) || 1, status: "active" });
            if (r.status === "SUCCESS") { toast("Boarding point added!"); closeBPDialog(); fetchBP(); }
        } catch (_) { toast("Error", "error"); } finally { setIsSubmitting(false); }
    };

    const handleCityStatusSubmit = async () => {
        const ns = confirmDialog.currentStatus === "active" ? "inactive" : "active";
        setIsSubmitting(true);
        try {
            const r = await putData(`${API_ENDPOINTS.updateCity}/${confirmDialog.cityId}`, { status: ns });
            if (r.status === "SUCCESS") { toast(`${confirmDialog.cityName} updated!`); setConfirmDialog({ open: false }); fetchCities(citySearch); }
        } catch (_) { toast("Update failed", "error"); } finally { setIsSubmitting(false); }
    };

    const handleBPStatusSubmit = async () => {
        const ns = confirmBPDialog.currentStatus === "active" ? "inactive" : "active";
        setIsSubmitting(true);
        try {
            const r = await putData(API_ENDPOINTS.updateBoardingPointStatus, { id: confirmBPDialog.id, status: ns });
            if (r.status === "SUCCESS") { toast(`${confirmBPDialog.pointName} updated!`); setConfirmBPDialog({ open: false }); fetchBP(); }
            else toast(r.message, "error");
        } catch (e) { toast(e.response?.data?.message || "Error", "error"); } finally { setIsSubmitting(false); }
    };

    return (
        <Box sx={{ bgcolor: T.bg, minHeight: "100vh", pb: 4 }}>

            {/* ── Hero header ── */}
            <Box sx={{
                background: `linear-gradient(135deg, ${T.teal} 0%, ${T.tealDk} 100%)`,
                borderRadius: "20px", p: 3, mb: 3,
                display: "flex", justifyContent: "space-between", alignItems: "center",
                boxShadow: "0 4px 20px rgba(0,124,132,0.2)", flexWrap: "wrap", gap: 2,
            }}>
                <Box>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#fff", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                        Delivery Zones
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.75)", mt: 0.5 }}>
                        Manage service cities and boarding points
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                    {[
                        { label: "Add City",           icon: <AddRoundedIcon sx={{ fontSize: 15 }} />,       onClick: () => setOpenCityDialog(true) },
                        { label: "Add Boarding Point", icon: <FmdGoodRoundedIcon sx={{ fontSize: 15 }} />,   onClick: () => setOpenBPDialog(true)   },
                        { label: "View on Map",        icon: <MapRoundedIcon sx={{ fontSize: 15 }} />,        onClick: () => navigate("/app/map-viewer") },
                    ].map(btn => (
                        <Button key={btn.label} variant="outlined" size="small" startIcon={btn.icon} onClick={btn.onClick}
                            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, fontSize: 12, color: "#fff", borderColor: "rgba(255,255,255,0.4)", bgcolor: "rgba(255,255,255,0.1)", "&:hover": { bgcolor: "rgba(255,255,255,0.2)", borderColor: "rgba(255,255,255,0.6)" } }}>
                            {btn.label}
                        </Button>
                    ))}
                </Box>
            </Box>

            {/* ── Tab toggle ── */}
            <Box sx={{ display: "flex", gap: 0.5, bgcolor: "white", border: `1px solid ${T.border}`, borderRadius: "14px", p: 0.75, mb: 3, width: "fit-content" }}>
                <TabBtn label="Cities"         count={citiesCount} active={tab === 0} onClick={() => setTab(0)} />
                <TabBtn label="Boarding Points" count={bpCount}    active={tab === 1} onClick={() => setTab(1)} />
            </Box>

            {/* ══ Cities tab ══ */}
            {tab === 0 && (
                <Box>
                    {/* Search */}
                    <TextField
                        placeholder="Search cities…" size="small" value={citySearch}
                        onChange={e => setCitySearch(e.target.value)}
                        sx={{ mb: 3, maxWidth: 400 }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 16, color: T.muted }} /></InputAdornment>,
                            endAdornment: citySearch ? <InputAdornment position="end"><IconButton size="small" onClick={() => setCitySearch("")}><ClearRoundedIcon sx={{ fontSize: 14 }} /></IconButton></InputAdornment> : null,
                            sx: { borderRadius: "12px", bgcolor: "white", fontSize: 13 },
                        }}
                    />

                    {loading ? (
                        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 2 }}>
                            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} variant="rounded" height={120} sx={{ borderRadius: "16px" }} />)}
                        </Box>
                    ) : cities.length === 0 ? (
                        <Empty icon={LocationCityRoundedIcon} msg="No Cities Found" sub={citySearch ? "Try a different search" : "Click 'Add City' to get started"} />
                    ) : (
                        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 2 }}>
                            {cities.map(city => (
                                <CityCard key={city.id} {...city} name={city.city} boardingCount={city.boardingPointsCount}
                                    onStatusUpdate={(id, name, status) => setConfirmDialog({ open: true, cityId: id, cityName: name, currentStatus: status })} />
                            ))}
                        </Box>
                    )}
                </Box>
            )}

            {/* ══ Boarding Points tab ══ */}
            {tab === 1 && (
                <Box>
                    {/* Filters */}
                    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", mb: 2.5 }}>
                        <TextField
                            placeholder="Search boarding points…" size="small" value={bpFilters.search}
                            onChange={e => setBpFilters(p => ({ ...p, search: e.target.value }))}
                            onKeyDown={e => { if (e.key === "Enter") fetchBP(); }}
                            sx={{ flex: "1 1 220px", maxWidth: 340 }}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 16, color: T.muted }} /></InputAdornment>,
                                sx: { borderRadius: "12px", bgcolor: "white", fontSize: 13 },
                            }}
                        />
                        <Autocomplete size="small" options={cities} getOptionLabel={o => o.city || ""}
                            onChange={(_, v) => setBpFilters(p => ({ ...p, city_id: v?.id || "" }))}
                            sx={{ width: 180 }}
                            renderInput={params => <TextField {...params} placeholder="Filter by city" InputProps={{ ...params.InputProps, sx: { borderRadius: "12px", bgcolor: "white", fontSize: 13 } }} />}
                        />
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <Select value={bpFilters.status} displayEmpty onChange={e => setBpFilters(p => ({ ...p, status: e.target.value }))}
                                sx={{ borderRadius: "12px", bgcolor: "white", fontSize: 13 }}>
                                <MenuItem value="">All Statuses</MenuItem>
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                            </Select>
                        </FormControl>
                        <Tooltip title="Reset filters">
                            <IconButton onClick={() => setBpFilters({ search: "", city_id: "", status: "" })}
                                sx={{ bgcolor: "white", border: `1px solid ${T.border}`, borderRadius: "10px" }}>
                                <RestartAltRoundedIcon sx={{ fontSize: 18, color: T.muted }} />
                            </IconButton>
                        </Tooltip>
                        {!loading && <Typography sx={{ fontSize: 13, color: T.muted, ml: "auto" }}>{bpCount} point{bpCount !== 1 ? "s" : ""}</Typography>}
                    </Box>

                    {/* Table */}
                    <Card sx={{ borderRadius: "16px", border: `1px solid ${T.border}`, boxShadow: "0 1px 4px rgba(15,23,42,0.06)", overflow: "hidden" }}>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: T.bg }}>
                                        {["Point Name", "City", "Address", "Position", "Status", ""].map(h => (
                                            <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11, color: T.muted, textTransform: "uppercase", letterSpacing: "0.4px", py: 1.5, borderBottom: `2px solid ${T.border}` }}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? (
                                        Array.from({ length: 8 }).map((_, i) => (
                                            <TableRow key={i}>
                                                {Array.from({ length: 6 }).map((__, j) => <TableCell key={j}><Skeleton height={18} /></TableCell>)}
                                            </TableRow>
                                        ))
                                    ) : boardingPoints.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} sx={{ py: 0, border: 0 }}>
                                                <Empty icon={FmdGoodRoundedIcon} msg="No Boarding Points" sub="Adjust filters or add a new point" />
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        boardingPoints.map(row => {
                                            const isActive = row.status === "active";
                                            return (
                                                <TableRow key={row.id} hover sx={{ "&:last-child td": { border: 0 } }}>
                                                    <TableCell sx={{ fontWeight: 700, fontSize: 13, color: T.text, maxWidth: 200 }}>
                                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200 }}>
                                                            {row.point}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        {row.City && (
                                                            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, bgcolor: T.tealLt, borderRadius: "7px", px: 1, py: 0.3 }}>
                                                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.teal }}>{row.City.city}</Typography>
                                                            </Box>
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ maxWidth: 240 }}>
                                                        <Tooltip title={row.address || ""}>
                                                            <Typography sx={{ fontSize: 12, color: T.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 240 }}>
                                                                {row.address || "—"}
                                                            </Typography>
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: "inline-flex", alignItems: "center", width: 28, height: 28, borderRadius: "8px", bgcolor: T.indigo + "18", justifyContent: "center" }}>
                                                            <Typography sx={{ fontSize: 12, fontWeight: 800, color: T.indigo }}>#{row.position}</Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1, py: 0.3, borderRadius: "7px", bgcolor: isActive ? T.emeraldLt : "#F1F5F9", border: `1px solid ${isActive ? T.emerald : T.border}22` }}>
                                                            <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: isActive ? T.emerald : T.muted }} />
                                                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: isActive ? T.emerald : T.muted, textTransform: "uppercase" }}>
                                                                {isActive ? "Active" : "Inactive"}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell padding="none">
                                                        <IconButton size="small" onClick={e => { setBpMenuAnchor(e.currentTarget); setActiveRow(row); }}>
                                                            <MoreVertIcon sx={{ fontSize: 18, color: T.muted }} />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Pagination */}
                        {!loading && bpCount > 0 && (
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2.5, py: 1.5, borderTop: `1px solid ${T.border}` }}>
                                <Typography sx={{ fontSize: 13, color: T.muted }}>
                                    Showing {Math.min(currentPage * perPage + 1, bpCount)}–{Math.min((currentPage + 1) * perPage, bpCount)} of {bpCount}
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Select value={perPage} onChange={e => { setPerPage(Number(e.target.value)); setCurrentPage(0); }} size="small" sx={{ borderRadius: "10px", fontSize: 13 }}>
                                        {[10, 25, 50].map(n => <MenuItem key={n} value={n}>{n} per page</MenuItem>)}
                                    </Select>
                                    <IconButton size="small" onClick={() => setCurrentPage(p => Math.max(p-1, 0))} disabled={currentPage === 0}
                                        sx={{ bgcolor: T.bg, border: `1px solid ${T.border}`, borderRadius: "8px" }}>
                                        <KeyboardArrowLeft sx={{ fontSize: 18 }} />
                                    </IconButton>
                                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.text, minWidth: 56, textAlign: "center" }}>
                                        {currentPage + 1} / {totalPages}
                                    </Typography>
                                    <IconButton size="small" onClick={() => setCurrentPage(p => Math.min(p+1, totalPages-1))} disabled={currentPage >= totalPages-1}
                                        sx={{ bgcolor: T.bg, border: `1px solid ${T.border}`, borderRadius: "8px" }}>
                                        <KeyboardArrowRight sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Box>
                            </Box>
                        )}
                    </Card>

                    {/* BP context menu */}
                    <Menu anchorEl={bpMenuAnchor} open={Boolean(bpMenuAnchor)} onClose={() => { setBpMenuAnchor(null); setActiveRow(null); }}
                        PaperProps={{ sx: { borderRadius: "12px", border: `1px solid ${T.border}`, boxShadow: "0 4px 16px rgba(0,0,0,0.1)" } }}>
                        <MenuItem onClick={() => { setConfirmBPDialog({ open: true, id: activeRow.id, pointName: activeRow.point, currentStatus: activeRow.status }); setBpMenuAnchor(null); setActiveRow(null); }}
                            sx={{ fontSize: 13, color: activeRow?.status === "active" ? T.rose : T.emerald, fontWeight: 600 }}>
                            {activeRow?.status === "active" ? "Deactivate" : "Activate"}
                        </MenuItem>
                    </Menu>
                </Box>
            )}

            {/* ── Dialogs ── */}
            {/* City status confirm */}
            <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ ...confirmDialog, open: false })} PaperProps={{ sx: { borderRadius: "16px" } }}>
                <DialogTitle sx={{ fontWeight: 700 }}>Update City Status</DialogTitle>
                <DialogContent><Typography fontSize={14}>Set <b>{confirmDialog.cityName}</b> to <b>{confirmDialog.currentStatus === "active" ? "Inactive" : "Active"}</b>?</Typography></DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={() => setConfirmDialog({ open: false })} sx={{ borderRadius: "10px", textTransform: "none" }}>Cancel</Button>
                    <Button onClick={handleCityStatusSubmit} variant="contained" disableElevation disabled={isSubmitting}
                        color={confirmDialog.currentStatus === "active" ? "error" : "success"}
                        sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700 }}>Confirm</Button>
                </DialogActions>
            </Dialog>

            {/* BP status confirm */}
            <Dialog open={confirmBPDialog.open} onClose={() => setConfirmBPDialog({ ...confirmBPDialog, open: false })} PaperProps={{ sx: { borderRadius: "16px" } }}>
                <DialogTitle sx={{ fontWeight: 700 }}>Update Point Status</DialogTitle>
                <DialogContent><Typography fontSize={14}>Set <b>{confirmBPDialog.pointName}</b> to <b>{confirmBPDialog.currentStatus === "active" ? "Inactive" : "Active"}</b>?</Typography></DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={() => setConfirmBPDialog({ open: false })} sx={{ borderRadius: "10px", textTransform: "none" }}>Cancel</Button>
                    <Button onClick={handleBPStatusSubmit} variant="contained" disableElevation disabled={isSubmitting}
                        color={confirmBPDialog.currentStatus === "active" ? "error" : "success"}
                        sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700 }}>Confirm</Button>
                </DialogActions>
            </Dialog>

            {/* Add City dialog */}
            <Dialog open={openCityDialog} onClose={closeCityDialog} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: "20px" } }}>
                <DialogTitle sx={{ fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Add City</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                        <Autocomplete options={cityAC.suggestions.data} getOptionLabel={o => o.description || ""} onInputChange={(_, v) => cityAC.setValue(v)} onChange={handleSelectCity}
                            renderInput={p => <TextField {...p} label="Search city (India)" size="small" InputProps={{ ...p.InputProps, sx: { borderRadius: "12px" } }} />} />
                        <TextField label="City" value={cityForm.city} fullWidth size="small" InputProps={{ readOnly: true, sx: { borderRadius: "12px" } }} />
                        <TextField label="State" value={cityForm.state} fullWidth size="small" InputProps={{ readOnly: true, sx: { borderRadius: "12px" } }} />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={closeCityDialog} sx={{ borderRadius: "10px", textTransform: "none" }}>Cancel</Button>
                    <Button onClick={handleAddCity} variant="contained" disableElevation disabled={isSubmitting || !cityForm.city}
                        sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, bgcolor: T.teal, "&:hover": { bgcolor: T.tealDk } }}>Add City</Button>
                </DialogActions>
            </Dialog>

            {/* Add BP dialog */}
            <Dialog open={openBPDialog} onClose={closeBPDialog} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "20px" } }}>
                <DialogTitle sx={{ fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Add Boarding Point</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                        <Autocomplete options={cities} getOptionLabel={o => o.city || ""} onChange={(_, v) => setBpForm(p => ({ ...p, city_id: v?.id || null, cityName: v?.city || "" }))}
                            renderInput={p => <TextField {...p} label="Select City" size="small" InputProps={{ ...p.InputProps, sx: { borderRadius: "12px" } }} />} />
                        <Autocomplete disabled={!bpForm.city_id} options={bpAC.suggestions.data} getOptionLabel={o => o.description || ""} onInputChange={(_, v) => bpAC.setValue(v)} onChange={handleSelectBP}
                            renderInput={p => <TextField {...p} label="Search location" size="small" InputProps={{ ...p.InputProps, sx: { borderRadius: "12px" } }} />} />
                        <Box sx={{ display: "flex", gap: 1.5 }}>
                            <TextField label="Point Name" fullWidth size="small" value={bpForm.point} onChange={e => setBpForm(p => ({ ...p, point: e.target.value }))} InputProps={{ sx: { borderRadius: "12px" } }} />
                            <TextField label="Position" type="number" sx={{ width: 120 }} size="small" value={bpForm.position} onChange={e => setBpForm(p => ({ ...p, position: e.target.value }))} InputProps={{ sx: { borderRadius: "12px" } }} />
                        </Box>
                        <TextField label="Address" fullWidth size="small" value={bpForm.address} multiline rows={2} InputProps={{ readOnly: true, sx: { borderRadius: "12px" } }} />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={closeBPDialog} sx={{ borderRadius: "10px", textTransform: "none" }}>Cancel</Button>
                    <Button onClick={handleAddBP} variant="contained" disableElevation disabled={isSubmitting || !bpForm.city_id || !bpForm.point}
                        sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, bgcolor: T.teal, "&:hover": { bgcolor: T.tealDk } }}>Add Point</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}>
                <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: "12px" }}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
}

export default LocationManagement;
